import React from 'react';
import type { ClearableSelectItem } from '../clearable-select';
import { ClearableSelect } from '../clearable-select';

/**
 * Enum representing the available score levels for filtering products
 */
enum ScoreLevel {
  All = 'all',
  VeryHigh = 'very_high',
  High = 'high',
  Moderate = 'moderate',
  Low = 'low',
  VeryLow = 'very_low',
}

// Dot colors by tier: green only at 80+, yellow at 60-79, orange at 40-59,
// red for 0-39. Moderate uses orange-400 (#F38744) — a touch warmer than the
// score-moderate accent token — per the filter's Figma spec.
const SCORE_LEVEL_ITEMS: ClearableSelectItem[] = [
  { id: ScoreLevel.VeryHigh, label: 'Optimized', range: '80-100', color: 'bg-success-400' },
  { id: ScoreLevel.High, label: 'Fair', range: '60-79', color: 'bg-yellow-400' },
  { id: ScoreLevel.Moderate, label: 'Moderate', range: '40-59', color: 'bg-orange-400' },
  { id: ScoreLevel.Low, label: 'Weak', range: '20-39', color: 'bg-error-500' },
  { id: ScoreLevel.VeryLow, label: 'Poor', range: '0-19', color: 'bg-error-500' },
];

interface ScoreLevelSelectProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /**
   * The selected score level
   * @optional
   * @type {ScoreLevel}
   * @default ScoreLevel.All
   */
  scoreLevel?: ScoreLevel;
  /**
   * The function to call when the score level changes
   * @required
   * @type {(scoreLevel: ScoreLevel) => void}
   */
  onScoreLevelChange: (scoreLevel: ScoreLevel) => void;
}

/**
 * Select component for choosing a score level filter.
 * Allows users to filter items by their listing score level (Poor, Weak, Moderate, Fair, Optimized, or All).
 *
 * @param props - The component props
 * @param props.scoreLevel - The currently selected score level
 * @param props.onScoreLevelChange - Callback fired when the score level changes
 */
function ScoreLevelSelect(props: ScoreLevelSelectProps) {
  const { scoreLevel = ScoreLevel.All, onScoreLevelChange, className, ...other } = props;

  const selectedKey = React.useMemo(() => (scoreLevel !== ScoreLevel.All ? scoreLevel : null), [scoreLevel]);

  const handleSelectionChange = (key: string | null) => {
    onScoreLevelChange((key ?? ScoreLevel.All) as ScoreLevel);
  };

  return (
    <ClearableSelect
      ariaLabel="Score level"
      placeholder="All scores"
      items={SCORE_LEVEL_ITEMS}
      selectedKey={selectedKey}
      onSelectionChange={handleSelectionChange}
      className={className}
      {...other}
    />
  );
}

export { ScoreLevelSelect, ScoreLevel };
export type { ScoreLevelSelectProps };
